import { useEffect, useState } from 'react';

/**
 * How far the two floating buttons should rise so they stop covering the footer.
 *
 * Returns the footer's currently visible height in px, capped at MAX_LIFT.
 * 0 whenever the footer is off screen, which is most of the time.
 *
 * ONE observer for the whole app, not one per caller. Today only the AI hub
 * subscribes (SupportWhatsapp moved to the top of the screen on 2026-10-11
 * and no longer needs to rise); kept shared so a second floating button can
 * join without running two IntersectionObservers over the same element. State lives at module
 * scope and the observers start with the first subscriber and stop with the
 * last, so a page that mounts neither button costs nothing.
 *
 * Every page renders its own <Footer/>, so the element is replaced on each
 * route change. A MutationObserver re-points the IntersectionObserver when that
 * happens; it is coalesced into one rAF because React commits fire many
 * mutations per render and re-targeting is only ever worth doing once per frame.
 */

// Past this the buttons would climb into the content above the footer, which
// looks worse than the overlap being fixed.
const MAX_LIFT = 120;

// The ratio of the footer that is visible, in 5% steps. IntersectionObserver
// only calls back when a threshold is crossed, so a single 0/1 threshold would
// snap the buttons up in one jump instead of letting them track the scroll.
const THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

const subscribers = new Set();
let lift = 0;
let io = null;
let mo = null;
let target = null;
let queued = false;

function publish(px) {
  const next = Math.min(Math.round(px), MAX_LIFT);
  if (next === lift) return;
  lift = next;
  subscribers.forEach((fn) => fn(lift));
}

function retarget() {
  queued = false;
  const el = document.querySelector('footer.site-footer');
  if (el === target) return;
  if (target) io.unobserve(target);
  target = el;
  // A page without a footer (chromeless, admin) leaves the buttons where they are.
  if (!target) { publish(0); return; }
  io.observe(target);
}

function queueRetarget() {
  if (queued) return;
  queued = true;
  window.requestAnimationFrame(retarget);
}

function start() {
  if (io) return;
  io = new IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    publish(entry.isIntersecting ? entry.intersectionRect.height : 0);
  }, { threshold: THRESHOLDS });
  mo = new MutationObserver(queueRetarget);
  mo.observe(document.body, { childList: true, subtree: true });
  retarget();
}

function stop() {
  if (mo) { mo.disconnect(); mo = null; }
  if (io) { io.disconnect(); io = null; }
  target = null;
  queued = false;
  lift = 0;
}

export default function useFooterLift() {
  const [value, setValue] = useState(lift);

  useEffect(() => {
    // Older browsers keep the buttons pinned rather than crashing on mount.
    if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    subscribers.add(setValue);
    start();
    setValue(lift);           // a late subscriber gets the current value, not 0
    return () => {
      subscribers.delete(setValue);
      if (subscribers.size === 0) stop();
    };
  }, []);

  return value;
}
